import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import { Badge } from "@/components/ui/badge";
import { Loader2, ChevronLeft, LayoutGrid, ListOrdered, FolderOpen, Users, Settings2 } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useBranding } from "@/contexts/BrandingContext";
import { CourseOverviewTab } from "@/components/t4r/course/CourseOverviewTab";
import { CourseLessonsTab } from "@/components/t4r/course/CourseLessonsTab";
import { CourseMaterialsTab } from "@/components/t4r/course/CourseMaterialsTab";
import { CourseLearnersTab } from "@/components/t4r/course/CourseLearnersTab";
import { CourseSettingsTab } from "@/components/t4r/course/CourseSettingsTab";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "lessons", label: "Lessons", icon: ListOrdered },
  { key: "materials", label: "Course materials", icon: FolderOpen },
  { key: "learners", label: "Learners", icon: Users },
  { key: "settings", label: "Settings", icon: Settings2 },
];

export default function T4RCourseDetailPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const { lms_name } = useBranding();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(false);
  useDocumentTitle(course?.title || "Course", "Course workspace", lms_name);

  const load = useCallback(() => {
    api.get(`/lms/admin/courses/${courseId}`).then(({ data }) => setCourse(data)).catch(() => setError(true));
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-rose-600" data-testid="course-not-found">Course not found. <button className="underline" onClick={() => navigate("/t4r/courses")}>Back to courses</button></p>;
  if (!course) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div>;

  const props = { course, reload: load };
  return (
    <div className="space-y-5" data-testid="t4r-course-detail">
      <button onClick={() => navigate("/t4r/courses")} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-courses"><ChevronLeft size={12} /> All courses</button>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-green-700">{course.award || "Course"}</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 truncate" data-testid="course-detail-title">{course.title}</h1>
        </div>
        <Badge variant="outline" className={`text-xs ${course.is_published ? "bg-green-100 text-green-800 border-green-200" : "bg-amber-50 text-amber-800 border-amber-200"}`} data-testid="course-detail-status">
          {course.is_published ? "Published" : "Draft — hidden from learners"}
        </Badge>
      </header>
      <nav className="flex gap-1 overflow-x-auto border-b border-border" role="tablist">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} role="tab" onClick={() => setParams({ tab: key })}
            className={`-mb-px inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors ${tab === key ? "border-green-600 text-green-800" : "border-transparent text-slate-500 hover:text-slate-900"}`}
            data-testid={`course-tab-${key}`}>
            <Icon size={14} /> {label}
            {key === "materials" && <span className="rounded-full bg-slate-100 px-1.5 text-[10px] text-slate-600">{course.material_count}</span>}
            {key === "learners" && <span className="rounded-full bg-slate-100 px-1.5 text-[10px] text-slate-600">{course.learner_count}</span>}
          </button>
        ))}
      </nav>
      {tab === "overview" && <CourseOverviewTab {...props} onTab={(t) => setParams({ tab: t })} />}
      {tab === "lessons" && <CourseLessonsTab {...props} />}
      {tab === "materials" && <CourseMaterialsTab {...props} />}
      {tab === "learners" && <CourseLearnersTab {...props} />}
      {tab === "settings" && <CourseSettingsTab {...props} />}
    </div>
  );
}
